import { createSlice } from '@reduxjs/toolkit';
import { callApi } from 'src/utils/apiCall';
import { calculatePreviousPeriod } from 'src/redux/slices/analyticsReports';

// Numeric fields decorated with previous_* by the period-comparison fetch.
// Exported so UserPerformanceTable's row-transform can iterate this list
// instead of enumerating each previous_* field explicitly — single source
// of truth, adding a new column means changing ONE place.
//
// Only includes fields that the table actually displays today. Other
// aggregates the backend returns (notes_created, follow_ups_created,
// status_changes) are intentionally NOT decorated since no column reads
// them — keeps the row payload focused.
export const COMPARISON_FIELDS = [
    'total_events',
    'leads_contacted',
    'appointments_booked',
    'appointments_attended',
    'appointments_fta',
    'follow_ups_completed',
    'whatsapp_sent',
    'whatsapp_received',
    'emails_sent',
    'sms_sent',
    'calls_logged',
    'payments_received',
    'leads_archived',
];

// Mock data was previously substituted into Redux when the API failed,
// which leaked fake user names ("Sarah Johnson", "James Chen") and fake
// activity counts into production UI during outages — indistinguishable
// from real staff to anyone who didn't know. Removed in favour of a
// real empty state + retry banner. See AnalyticsReportsByTeam.jsx for
// the handleRetry callback and UserPerformanceTable / UserEventFeed
// for the error/retry UI.

const INITIAL_STATE = {
    isLoading: false,
    error: null,
    // Cache keys to prevent redundant re-fetches when navigating between tabs
    _userPerfFetchKey: null,
    _userEventsFetchKey: null,
    kpis: {},
    userPerformance: [],
    selectedUserId: null,
    userEvents: {
        isLoading: false,
        results: [],
        count: 0,
        next: null,
        previous: null,
    },
    eventFilters: {
        eventType: '',
        channel: '',
        practiceUser: '',
        page: 1,
    },
};

const slice = createSlice({
    name: 'userPerformance',
    initialState: INITIAL_STATE,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setFetchKey: (state, action) => {
            const { key, value } = action.payload;
            state[key] = value;
        },
        setKpis: (state, action) => {
            state.kpis = action.payload;
        },
        setUserPerformance: (state, action) => {
            state.userPerformance = action.payload;
        },
        setSelectedUserId: (state, action) => {
            state.selectedUserId = action.payload;
        },
        setUserEvents: (state, action) => {
            state.userEvents = { ...state.userEvents, ...action.payload };
        },
        setEventFilters: (state, action) => {
            state.eventFilters = { ...state.eventFilters, ...action.payload };
        },
        resetEventFilters: (state) => {
            state.eventFilters = INITIAL_STATE.eventFilters;
        },
    },
});

export const {
    setIsLoading,
    setError,
    setFetchKey,
    setKpis,
    setUserPerformance,
    setSelectedUserId,
    setUserEvents,
    setEventFilters,
    resetEventFilters,
} = slice.actions;

// Note on unification with analyticsReports slice:
// The phase-5 audit flagged this slice as a candidate to merge into
// analyticsReports. Re-examining after reporting shipped: ByTeam already
// reads `state.analyticsReports.dateRange` as its source of truth for the
// date range and writes back via `persistDateRange(analyticsReports)`, so
// there's NO date-range desync between tabs. This slice is essentially
// data-only — 2 thunks and a few selectors — and keeping it separate
// keeps analyticsReports.js from ballooning past 3k lines. Explicitly
// staying split.
//
// If a future feature needs ByTeam actions to interleave with analytics
// actions (e.g. an optimistic update that touches both), revisit.

// Thunks

export function fetchUserPerformanceData(practiceIdOrIds, dateParams, comparisonType = 'period') {
    const ids = Array.isArray(practiceIdOrIds)
        ? practiceIdOrIds.filter((id) => id != null)
        : [practiceIdOrIds].filter((id) => id != null);
    const practiceId = ids[0];
    // UserPerformanceViewSet supports practice__in — aggregates events/leads
    // across every selected practice in a single request. Both the current
    // and previous-period fetches share the same multi-practice scope.
    const pInQuery = ids.length > 1 ? `&practice__in=${ids.join(',')}` : '';
    return async (dispatch, getState) => {
        if (!practiceId) return;
        const startDate = dateParams.start_date;
        const endDate = dateParams.end_date;
        // Cache key includes comparisonType so toggling period↔YoY forces a
        // refetch instead of returning stale-keyed data.
        const cacheKey = `${ids.join(',')}-${startDate}-${endDate}-${comparisonType}`;

        // Capture the key up front so isStale() can verify at each await
        // boundary that the request is still current. Prevents a slow
        // previous-period fetch from clobbering a more recent selection.
        const isStale = () => getState().userPerformance._userPerfFetchKey !== cacheKey;

        // Reserve the slot BEFORE any network call so identical in-flight
        // dispatches see the key match and stop early.
        dispatch(setFetchKey({ key: '_userPerfFetchKey', value: cacheKey }));
        dispatch(setIsLoading(true));
        dispatch(setError(null));

        const base = `/api/v1/practices/${practiceId}/reports/user-performance/`;
        const currentUrl = `${base}?start_date=${startDate}&end_date=${endDate}${pInQuery}`;

        // comparisonType='off' short-circuits the second fetch entirely —
        // the user explicitly turned comparison off in the header to keep
        // the table clean. previousPromise stays null; the "decorator"
        // phase below skips when it's null and no previous_* fields are
        // attached to rows, so Δ chips simply don't render. No special
        // case downstream — the existing "previous fetch in flight"
        // codepath already handles missing previous_* gracefully.
        const comparisonEnabled = comparisonType === 'period' || comparisonType === 'yoy';
        let previousPromise = null;
        if (comparisonEnabled) {
            // Previous period derived from the current range via the shared
            // helper. calculatePreviousPeriod returns "YYYY-MM-DD HH:mm:ss" —
            // the endpoint accepts the date prefix, so slice to YYYY-MM-DD
            // for consistency with the current-period call.
            const prevRange = calculatePreviousPeriod(
                { start: startDate, end: endDate },
                comparisonType,
            );
            const prevStartDate = prevRange.start.slice(0, 10);
            const prevEndDate = prevRange.end.slice(0, 10);
            const previousUrl = `${base}?start_date=${prevStartDate}&end_date=${prevEndDate}${pInQuery}`;
            previousPromise = callApi(previousUrl);
        }

        // Fire current in parallel with (optional) previous — UI gets
        // primary data as soon as `current` resolves; `previous` decorates
        // rows when it lands, or is skipped when comparison is off.
        const currentPromise = callApi(currentUrl);

        // ── Current period ──────────────────────────────────────────────
        let currentRows = [];
        let failed = false;
        try {
            const response = await currentPromise;
            if (isStale()) return;
            const data = response?.data;
            if (response?.ok && data) {
                currentRows = Array.isArray(data) ? data : (data?.results || data?.user_performance || []);
                dispatch(setUserPerformance(currentRows));
                dispatch(setKpis(data?.kpis || data?.summary || {}));
            } else {
                // API rejected the request (5xx, 403, etc.) — emit an explicit
                // error and an empty rowset. The UI surfaces "Failed to load
                // team performance — Retry" rather than silently substituting
                // fake names.
                const message = response?.error || `Failed to load team performance (HTTP ${response?.status || 'unknown'})`;
                console.warn('[userPerformance] current fetch failed:', message);
                failed = true;
                dispatch(setUserPerformance([]));
                dispatch(setKpis({}));
                dispatch(setError(message));
            }
        } catch (error) {
            if (isStale()) return;
            const message = error?.message || 'Network error loading team performance';
            console.warn('[userPerformance] current fetch error:', message);
            failed = true;
            dispatch(setUserPerformance([]));
            dispatch(setKpis({}));
            dispatch(setError(message));
        }

        // Current data is rendered (or empty + error) — un-block the page.
        dispatch(setIsLoading(false));

        // ── Previous period (background decorator) ──────────────────────
        // Skip when current failed — no rows to decorate, and chasing a
        // previous-period fetch we likely can't satisfy either is wasteful.
        // Also skip when comparison is off — previousPromise is null.
        if (failed || !previousPromise) return;
        try {
            const prevResponse = await previousPromise;
            if (isStale()) return;
            const prevData = prevResponse?.data;
            if (!prevResponse?.ok || !prevData) return;
            const prevRows = Array.isArray(prevData)
                ? prevData
                : (prevData?.results || prevData?.user_performance || []);

            // Index previous rows by practice_user_id for O(1) merge.
            const prevIndex = new Map(prevRows.map((r) => [r.practice_user_id, r]));

            // Decorate every current row with previous_* mirror fields.
            // Users in current-but-not-previous get no decoration → no chip.
            // Users in previous-but-not-current are dropped (page = "active team").
            const decorated = currentRows.map((row) => {
                const prev = prevIndex.get(row.practice_user_id);
                if (!prev) return row;
                const decoration = {};
                COMPARISON_FIELDS.forEach((field) => {
                    decoration[`previous_${field}`] = prev[field] ?? 0;
                });
                return { ...row, ...decoration };
            });

            // The page's derivedKpis useMemo (AnalyticsReportsByTeam.jsx)
            // sums previous_* fields across decorated rows to populate each
            // KPI card's previousValue. We don't pre-aggregate here — single
            // source of truth lives on the page, no duplicate-path drift
            // between slice and view layer.
            dispatch(setUserPerformance(decorated));
        } catch (error) {
            if (isStale()) return;
            // Previous-period failure is non-fatal: current data is fine,
            // chips just don't appear. Log and move on.
            console.warn('[userPerformance] previous-period fetch failed:', error?.message);
        }
    };
}

export function fetchUserEvents(practiceId, filters = {}) {
    return async (dispatch) => {
        dispatch(setUserEvents({ isLoading: true }));
        dispatch(setError(null));

        try {
            const params = new URLSearchParams();
            if (filters.event_type) params.append('event_type', filters.event_type);
            if (filters.channel) params.append('channel', filters.channel);
            if (filters.practice_user) params.append('practice_user', filters.practice_user);
            if (filters.page) params.append('page', filters.page);
            if (filters.page_size) params.append('page_size', filters.page_size);

            const queryString = params.toString();
            const url = `/api/v1/practices/${practiceId}/reports/user-events/${queryString ? `?${queryString}` : ''}`;

            const response = await callApi(url);
            const data = response?.data;

            if (response?.ok && data) {
                dispatch(setUserEvents({
                    isLoading: false,
                    results: data?.results || [],
                    count: data?.count || 0,
                    next: data?.next || null,
                    previous: data?.previous || null,
                }));
            } else {
                // API rejected — empty results + error message.
                const message = response?.error || `Failed to load events (HTTP ${response?.status || 'unknown'})`;
                console.warn('[userEvents] fetch failed:', message);
                dispatch(setUserEvents({
                    isLoading: false,
                    results: [],
                    count: 0,
                    next: null,
                    previous: null,
                }));
                dispatch(setError(message));
            }

            const filterKey = JSON.stringify({ practiceId, ...filters });
            dispatch(setFetchKey({ key: '_userEventsFetchKey', value: filterKey }));
        } catch (error) {
            const message = error?.message || 'Network error loading events';
            console.warn('[userEvents] fetch error:', message);
            dispatch(setUserEvents({
                isLoading: false,
                results: [],
                count: 0,
                next: null,
                previous: null,
            }));
            dispatch(setError(message));
        }
    };
}

/**
 * One page of the By Team drill-down, straight from UserEvent.
 *
 * Deliberately NOT `fetchUserEvents` above: that one powers UserEventFeed and
 * writes shared slice state, sends page/page_size (silently ignored — the
 * backend is LimitOffsetPagination) and sends no date filters at all. Reusing
 * it would reintroduce the very count mismatch this change exists to remove.
 *
 * Returns a value rather than dispatching, so the popup owns its own state and
 * cannot clobber the Event Feed.
 */
export async function fetchUserEventDrilldown({
    practiceId, practiceUserId, eventType, startDate, endDate, limit = 200, offset = 0,
}) {
    const params = new URLSearchParams();
    if (eventType) params.append('event_type', eventType);
    // Absent on the KPI-card drilldowns, which are practice-wide.
    if (practiceUserId) params.append('practice_user', String(practiceUserId));
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('limit', String(limit));
    if (offset) params.append('offset', String(offset));

    const url = `/api/v1/practices/${practiceId}/reports/user-events/?${params.toString()}`;
    const response = await callApi(url);
    const data = response?.data;

    if (response?.ok && data) {
        return { ok: true, count: data.count || 0, results: data.results || [], error: null };
    }
    return {
        ok: false,
        count: 0,
        results: [],
        error: response?.error || `Failed to load events (HTTP ${response?.status || 'unknown'})`,
    };
}

export default slice.reducer;
